
import { useKnowledgeEntryForm } from '@/hooks/useKnowledgeEntryForm';
import { useUserRole } from '@/hooks/useUserRole';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFields } from '../form-sections/FormFields';
import { UploadDropzone } from './UploadDropzone';
import { Button } from '@/components/ui/button';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useCallback, useEffect } from 'react';
import { ProcessingStage } from './ProcessingStages';
import { UploadProgress } from './UploadProgress';

interface UploadKnowledgeFormProps {
  onEntryCreated?: () => void;
}

export function UploadKnowledgeForm({
  onEntryCreated
}: UploadKnowledgeFormProps) {
  const { isAdmin } = useUserRole();
  const [processingStage, setProcessingStage] = useState<ProcessingStage>("uploading");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<{ isError: boolean, message: string }>({ 
    isError: false, 
    message: '' 
  });
  
  // Debug logging on mount
  useEffect(() => {
    console.log('[DEBUG] UploadKnowledgeForm mounted');
    return () => {
      console.log('[DEBUG] UploadKnowledgeForm unmounted');
    };
  }, []);

  // Handle stage changes from the form submission process
  const handleProcessingStageChange = useCallback((stage: string, progress: number) => {
    console.log('[DEBUG] Processing stage change:', { stage, progress });
    setProcessingStage(stage as ProcessingStage);
    setUploadProgress(progress);
    
    // If the process is complete, reset after a delay
    if (stage === "complete" && progress === 100) {
      setTimeout(() => {
        setUploadProgress(0);
        setProcessingStage("uploading");
      }, 3000); // Reset after 3 seconds of showing complete
    }
  }, []);

  const {
    tags,
    notes,
    selectedFile,
    isSubmitting,
    handleSubmit,
    handleFileChange,
    handleTagsChange,
    handleNotesChange,
    clearSelectedFile
  } = useKnowledgeEntryForm({
    onEntryCreated: async () => {
      console.log('[DEBUG] Entry created callback called');
      if (onEntryCreated) await onEntryCreated();
    },
    onError: (error) => {
      console.error('[DEBUG] Form submission error:', error);
      setError({ isError: true, message: error.message });
      toast.error('Failed to create knowledge entry. Please try again.');
      
      // Reset the progress UI after error
      setTimeout(() => {
        setUploadProgress(0);
        setProcessingStage("uploading");
        setError({ isError: false, message: '' });
      }, 5000);
    },
    onProcessingStageChange: handleProcessingStageChange
  });

  if (!isAdmin) {
    return null;
  }

  console.log('[DEBUG] Rendering UploadKnowledgeForm, isSubmitting:', isSubmitting);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Upload Knowledge</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => {
          console.log('[DEBUG] Form submitted');
          handleSubmit(e);
        }} className="space-y-4">
          <FormFields 
            title="" 
            content="" 
            tags={tags}
            notes={notes}
            selectedFile={selectedFile} 
            onTitleChange={() => {}} 
            onContentChange={() => {}} 
            onTagsChange={handleTagsChange}
            onNotesChange={handleNotesChange}
            hideContent 
            hideTitle 
          />
          
          <UploadDropzone 
            onFileSelect={(file) => {
              console.log('[DEBUG] File selected via dropzone:', file instanceof File ? file.name : 'Not a file');
              handleFileChange(file);
            }} 
          />

          <Button 
            type="submit" 
            disabled={isSubmitting || !selectedFile} 
            className="w-full"
          >
            {isSubmitting ? 'Processing...' : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload Knowledge Entry
              </>
            )}
          </Button>
          
          {isSubmitting && (
            <UploadProgress 
              stage={processingStage}
              progress={uploadProgress}
              isUploading={isSubmitting}
              isError={error.isError}
              errorMessage={error.message}
            />
          )}
        </form>
      </CardContent>
    </Card>
  );
}
